import { c as _c } from "react/compiler-runtime";
import { Bookmark, Copy, Eye, Heart, MessageCircle, MessagesSquare, Repeat2, Share, Share2 } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn, formatNumber } from '@/lib/utils';
import type { TweetMetrics } from '@/types/tweet';
export interface TweetActionStates {
  isBookmarked: boolean;
  isLiked: boolean;
  isLoading?: boolean;
  isRetweeted: boolean;
}
interface TweetActionsProperties {
  compact?: boolean;
  conversationCount?: number;
  enableActions?: boolean;
  metrics: TweetMetrics;
  onBookmark?: () => void;
  onCopyLink?: () => void;
  onLike?: () => void;
  onOpenConversation?: () => void;
  onReply?: () => void;
  onRetweet?: () => void;
  onShare?: () => void;
  states: TweetActionStates;
}
export function TweetActions(t0) {
  const $ = _c(140);
  const {
    metrics,
    states,
    onReply,
    onRetweet,
    onLike,
    onBookmark,
    onShare,
    onCopyLink,
    enableActions: t1,
    compact: t2,
    conversationCount,
    onOpenConversation
  } = t0;
  const enableActions = t1 === undefined ? true : t1;
  const compact = t2 === undefined ? false : t2;
  const buttonSize = compact ? "h-7 px-1 rounded-lg" : "h-8 px-1 sm:px-1.5 rounded-lg";
  const iconSize = compact ? "h-3 w-3" : "h-3.5 w-3.5 sm:h-4 sm:w-4";
  const textSize = compact ? "text-[10px]" : "text-[11px] sm:text-xs";
  const countNumberClasses = textSize;
  const gapSize = compact ? "gap-0.5" : "gap-0.5 sm:gap-1";
  let t3;
  let totalRetweets;
  if ($[0] !== buttonSize || $[1] !== conversationCount || $[2] !== countNumberClasses || $[3] !== gapSize || $[4] !== iconSize || $[5] !== metrics.quoteCount || $[6] !== metrics.retweetCount || $[7] !== onOpenConversation || $[8] !== textSize) {
    const passivePillClasses = cn("inline-flex items-center rounded-lg border border-transparent text-muted-foreground select-none bg-transparent", buttonSize, gapSize, textSize);
    totalRetweets = metrics.retweetCount + metrics.quoteCount;
    let t4;
    if ($[11] !== conversationCount) {
      t4 = typeof conversationCount === "number" ? formatNumber(conversationCount) : null;
      $[11] = conversationCount;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    const conversationDisplay = t4;
    t3 = conversationDisplay === null ? null : <PreloadedConversationBadge key={conversationCount} className={cn(passivePillClasses, onOpenConversation ? "cursor-pointer hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background" : "cursor-default")} iconClassName={iconSize} textClassName={countNumberClasses} displayValue={conversationDisplay} onClick={() => onOpenConversation?.()} ariaLabel={onOpenConversation ? `Open conversation thread, ${conversationDisplay} messages` : `${conversationDisplay} messages`} />;
    $[0] = buttonSize;
    $[1] = conversationCount;
    $[2] = countNumberClasses;
    $[3] = gapSize;
    $[4] = iconSize;
    $[5] = metrics.quoteCount;
    $[6] = metrics.retweetCount;
    $[7] = onOpenConversation;
    $[8] = textSize;
    $[9] = t3;
    $[10] = totalRetweets;
  } else {
    t3 = $[9];
    totalRetweets = $[10];
  }
  const conversationBadge = t3;
  let t4;
  if ($[13] !== metrics.replyCount) {
    t4 = metrics.replyCount > 0 ? formatNumber(metrics.replyCount) : null;
    $[13] = metrics.replyCount;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const replyDisplay = t4;
  let t5;
  if ($[15] !== totalRetweets) {
    t5 = totalRetweets > 0 ? formatNumber(totalRetweets) : null;
    $[15] = totalRetweets;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const retweetDisplay = t5;
  let t6;
  if ($[17] !== metrics.likeCount) {
    t6 = metrics.likeCount > 0 ? formatNumber(metrics.likeCount) : null;
    $[17] = metrics.likeCount;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const likeDisplay = t6;
  const bookmarkCount = metrics.bookmarkCount ?? 0;
  let t7;
  if ($[19] !== bookmarkCount) {
    t7 = bookmarkCount > 0 ? formatNumber(bookmarkCount) : null;
    $[19] = bookmarkCount;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const bookmarkDisplay = t7;
  let t8;
  if ($[21] !== metrics.impressionCount) {
    t8 = typeof metrics.impressionCount === "number" && metrics.impressionCount > 0 ? formatNumber(metrics.impressionCount) : null;
    $[21] = metrics.impressionCount;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const impressionDisplay = t8;
  const t9 = !compact && "xs:gap-3 sm:gap-4 sm:mt-3 -ml-2 sm:ml-0";
  let t10;
  if ($[23] !== t9) {
    t10 = cn("mt-2 flex w-full items-center gap-1", t9);
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== buttonSize || $[26] !== gapSize) {
    t11 = cn(buttonSize, gapSize, "text-muted-foreground hover:text-primary");
    $[25] = buttonSize;
    $[26] = gapSize;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = enableActions ? onReply : undefined;
  const t13 = !enableActions;
  const t14 = `Reply to tweet, ${metrics.replyCount} replies`;
  let t15;
  if ($[28] !== iconSize) {
    t15 = <MessageCircle className={iconSize} aria-hidden="true" />;
    $[28] = iconSize;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== countNumberClasses || $[31] !== replyDisplay) {
    t16 = replyDisplay && <RollingCount className={countNumberClasses} displayValue={replyDisplay} />;
    $[30] = countNumberClasses;
    $[31] = replyDisplay;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t14 || $[37] !== t15 || $[38] !== t16) {
    t17 = <Button variant="ghost" size="sm" className={t11} onClick={t12} disabled={t13} aria-label={t14} data-testid="reply-button">{t15}{t16}</Button>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const t18 = states.isRetweeted ? "text-success" : "text-muted-foreground hover:text-success";
  let t19;
  if ($[40] !== buttonSize || $[41] !== gapSize || $[42] !== t18) {
    t19 = cn(buttonSize, gapSize, t18);
    $[40] = buttonSize;
    $[41] = gapSize;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const t20 = enableActions ? onRetweet : undefined;
  const t21 = !enableActions || states.isRetweeted;
  const t22 = `${states.isRetweeted ? "Retweeted" : "Retweet"}, ${totalRetweets} retweets`;
  let t23;
  if ($[44] !== iconSize) {
    t23 = <Repeat2 className={iconSize} aria-hidden="true" />;
    $[44] = iconSize;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== countNumberClasses || $[47] !== retweetDisplay) {
    t24 = retweetDisplay && <RollingCount className={countNumberClasses} displayValue={retweetDisplay} />;
    $[46] = countNumberClasses;
    $[47] = retweetDisplay;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== states.isRetweeted || $[50] !== t19 || $[51] !== t20 || $[52] !== t21 || $[53] !== t22 || $[54] !== t23 || $[55] !== t24) {
    t25 = <Button variant="ghost" size="sm" className={t19} onClick={t20} disabled={t21} aria-label={t22} aria-pressed={states.isRetweeted} data-testid="retweet-button">{t23}{t24}</Button>;
    $[49] = states.isRetweeted;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  const t26 = states.isLiked ? "text-destructive" : "text-muted-foreground hover:text-destructive";
  let t27;
  if ($[57] !== buttonSize || $[58] !== gapSize || $[59] !== t26) {
    t27 = cn(buttonSize, gapSize, t26);
    $[57] = buttonSize;
    $[58] = gapSize;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  const t28 = enableActions ? onLike : undefined;
  const t29 = !enableActions;
  const t30 = `${states.isLiked ? "Unlike" : "Like"} tweet, ${metrics.likeCount} likes`;
  const t31 = states.isLiked;
  const t32 = states.isLiked && "fill-current";
  let t33;
  if ($[61] !== iconSize || $[62] !== t32) {
    t33 = cn(iconSize, t32);
    $[61] = iconSize;
    $[62] = t32;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== t33) {
    t34 = <Heart className={t33} aria-hidden="true" />;
    $[64] = t33;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== countNumberClasses || $[67] !== likeDisplay) {
    t35 = likeDisplay && <RollingCount className={countNumberClasses} displayValue={likeDisplay} />;
    $[66] = countNumberClasses;
    $[67] = likeDisplay;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== states.isLiked || $[70] !== t27 || $[71] !== t28 || $[72] !== t29 || $[73] !== t30 || $[74] !== t34 || $[75] !== t35) {
    t36 = <Button variant="ghost" size="sm" className={t27} onClick={t28} disabled={t29} aria-label={t30} aria-pressed={t31} data-testid="like-button">{t34}{t35}</Button>;
    $[69] = states.isLiked;
    $[70] = t27;
    $[71] = t28;
    $[72] = t29;
    $[73] = t30;
    $[74] = t34;
    $[75] = t35;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  const t37 = states.isBookmarked ? "text-primary" : "text-muted-foreground hover:text-primary";
  let t38;
  if ($[77] !== buttonSize || $[78] !== gapSize || $[79] !== t37) {
    t38 = cn(buttonSize, gapSize, t37);
    $[77] = buttonSize;
    $[78] = gapSize;
    $[79] = t37;
    $[80] = t38;
  } else {
    t38 = $[80];
  }
  const t39 = enableActions ? onBookmark : undefined;
  const t40 = !enableActions;
  const t41 = `${states.isBookmarked ? "Remove bookmark" : "Bookmark tweet"}${metrics.bookmarkCount ? `, ${metrics.bookmarkCount} bookmarks` : ""}`;
  const t42 = states.isBookmarked && "fill-current";
  let t43;
  if ($[81] !== iconSize || $[82] !== t42) {
    t43 = cn(iconSize, t42);
    $[81] = iconSize;
    $[82] = t42;
    $[83] = t43;
  } else {
    t43 = $[83];
  }
  let t44;
  if ($[84] !== t43) {
    t44 = <Bookmark className={t43} />;
    $[84] = t43;
    $[85] = t44;
  } else {
    t44 = $[85];
  }
  let t45;
  if ($[86] !== bookmarkDisplay || $[87] !== countNumberClasses) {
    t45 = bookmarkDisplay && <RollingCount className={countNumberClasses} displayValue={bookmarkDisplay} />;
    $[86] = bookmarkDisplay;
    $[87] = countNumberClasses;
    $[88] = t45;
  } else {
    t45 = $[88];
  }
  let t46;
  if ($[89] !== t38 || $[90] !== t39 || $[91] !== t40 || $[92] !== t41 || $[93] !== t44 || $[94] !== t45) {
    t46 = <Button variant="ghost" size="sm" className={t38} onClick={t39} disabled={t40} aria-label={t41} data-testid="bookmark-button">{t44}{t45}</Button>;
    $[89] = t38;
    $[90] = t39;
    $[91] = t40;
    $[92] = t41;
    $[93] = t44;
    $[94] = t45;
    $[95] = t46;
  } else {
    t46 = $[95];
  }
  let t47;
  if ($[96] !== countNumberClasses || $[97] !== gapSize || $[98] !== iconSize || $[99] !== impressionDisplay) {
    t47 = impressionDisplay && <div className={cn("inline-flex items-center", gapSize, countNumberClasses, "text-muted-foreground select-none")}><Eye className={iconSize} aria-hidden="true" /><RollingCount className={countNumberClasses} displayValue={impressionDisplay} /></div>;
    $[96] = countNumberClasses;
    $[97] = gapSize;
    $[98] = iconSize;
    $[99] = impressionDisplay;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] !== buttonSize) {
    t48 = cn(buttonSize, "ml-auto text-muted-foreground hover:text-primary");
    $[101] = buttonSize;
    $[102] = t48;
  } else {
    t48 = $[102];
  }
  const t49 = !enableActions;
  let t50;
  if ($[103] !== iconSize) {
    t50 = <Share className={iconSize} />;
    $[103] = iconSize;
    $[104] = t50;
  } else {
    t50 = $[104];
  }
  let t51;
  if ($[105] !== t48 || $[106] !== t49 || $[107] !== t50) {
    t51 = <DropdownMenuTrigger asChild={true}><Button variant="ghost" size="sm" className={t48} disabled={t49} aria-label="Share tweet" data-testid="share-button">{t50}</Button></DropdownMenuTrigger>;
    $[105] = t48;
    $[106] = t49;
    $[107] = t50;
    $[108] = t51;
  } else {
    t51 = $[108];
  }
  const t52 = !enableActions;
  let t53;
  if ($[109] !== enableActions || $[110] !== onCopyLink) {
    t53 = () => {
      if (!enableActions) {
        return;
      }
      onCopyLink?.();
    };
    $[109] = enableActions;
    $[110] = onCopyLink;
    $[111] = t53;
  } else {
    t53 = $[111];
  }
  let t54;
  let t55;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <Copy className="mr-2 h-4 w-4" aria-hidden="true" />;
    t55 = <span>Copy link</span>;
    $[112] = t54;
    $[113] = t55;
  } else {
    t54 = $[112];
    t55 = $[113];
  }
  let t56;
  if ($[114] !== t52 || $[115] !== t53) {
    t56 = <DropdownMenuItem disabled={t52} onSelect={t53}>{t54}{t55}</DropdownMenuItem>;
    $[114] = t52;
    $[115] = t53;
    $[116] = t56;
  } else {
    t56 = $[116];
  }
  const t57 = !enableActions;
  let t58;
  if ($[117] !== enableActions || $[118] !== onShare) {
    t58 = () => {
      if (!enableActions) {
        return;
      }
      onShare?.();
    };
    $[117] = enableActions;
    $[118] = onShare;
    $[119] = t58;
  } else {
    t58 = $[119];
  }
  let t59;
  let t60;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <Share2 className="mr-2 h-4 w-4" aria-hidden="true" />;
    t60 = <span>Share...</span>;
    $[120] = t59;
    $[121] = t60;
  } else {
    t59 = $[120];
    t60 = $[121];
  }
  let t61;
  if ($[122] !== t57 || $[123] !== t58) {
    t61 = <DropdownMenuItem disabled={t57} onSelect={t58}>{t59}{t60}</DropdownMenuItem>;
    $[122] = t57;
    $[123] = t58;
    $[124] = t61;
  } else {
    t61 = $[124];
  }
  let t62;
  if ($[125] !== t56 || $[126] !== t61) {
    t62 = <DropdownMenuContent align="end" sideOffset={6} className="w-44">{t56}{t61}</DropdownMenuContent>;
    $[125] = t56;
    $[126] = t61;
    $[127] = t62;
  } else {
    t62 = $[127];
  }
  let t63;
  if ($[128] !== t51 || $[129] !== t62) {
    t63 = <DropdownMenu>{t51}{t62}</DropdownMenu>;
    $[128] = t51;
    $[129] = t62;
    $[130] = t63;
  } else {
    t63 = $[130];
  }
  let t64;
  if ($[131] !== conversationBadge || $[132] !== t10 || $[133] !== t17 || $[134] !== t25 || $[135] !== t36 || $[136] !== t46 || $[137] !== t47 || $[138] !== t63) {
    t64 = <div className={t10}>{t17}{t25}{t36}{t46}{t47}{conversationBadge}{t63}</div>;
    $[131] = conversationBadge;
    $[132] = t10;
    $[133] = t17;
    $[134] = t25;
    $[135] = t36;
    $[136] = t46;
    $[137] = t47;
    $[138] = t63;
    $[139] = t64;
  } else {
    t64 = $[139];
  }
  return t64;
}
interface PreloadedConversationBadgeProperties {
  ariaLabel?: string;
  className: string;
  displayValue: string;
  iconClassName: string;
  onClick?: () => void;
  textClassName: string;
}
function PreloadedConversationBadge(t0) {
  const $ = _c(30);
  const {
    className,
    iconClassName,
    textClassName,
    displayValue,
    onClick,
    ariaLabel
  } = t0;
  const [visible, setVisible] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const id = requestAnimationFrame(() => {
        setVisible(true);
      });
      return () => {
        cancelAnimationFrame(id);
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const interactive = typeof onClick === "function";
  if (interactive) {
    const t3 = visible && "opacity-100";
    let t4;
    if ($[2] !== className || $[3] !== t3) {
      t4 = cn(className, "opacity-0 transition-opacity duration-200 ease-out", t3);
      $[2] = className;
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <span className="sr-only">Preloaded conversations:</span>;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== iconClassName) {
      t6 = <MessagesSquare className={iconClassName} aria-hidden="true" />;
      $[6] = iconClassName;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== displayValue || $[9] !== textClassName) {
      t7 = <RollingCount ariaHidden={true} className={textClassName} displayValue={displayValue} />;
      $[8] = displayValue;
      $[9] = textClassName;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== ariaLabel || $[12] !== onClick || $[13] !== t4 || $[14] !== t6 || $[15] !== t7) {
      t8 = <button type="button" className={t4} data-testid="preloaded-conversation-indicator" onClick={onClick} aria-label={ariaLabel}>{t5}{t6}{t7}</button>;
      $[11] = ariaLabel;
      $[12] = onClick;
      $[13] = t4;
      $[14] = t6;
      $[15] = t7;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    return t8;
  }
  const t3 = visible && "opacity-100";
  let t4;
  if ($[17] !== className || $[18] !== t3) {
    t4 = cn(className, "opacity-0 transition-opacity duration-200 ease-out", t3);
    $[17] = className;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="sr-only">Preloaded conversations:</span>;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== iconClassName) {
    t6 = <MessagesSquare className={iconClassName} aria-hidden="true" />;
    $[21] = iconClassName;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== displayValue || $[24] !== textClassName) {
    t7 = <RollingCount ariaHidden={true} className={textClassName} displayValue={displayValue} />;
    $[23] = displayValue;
    $[24] = textClassName;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t4 || $[27] !== t6 || $[28] !== t7) {
    t8 = <span className={t4} data-testid="preloaded-conversation-indicator">{t5}{t6}{t7}</span>;
    $[26] = t4;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
interface RollingCountProperties {
  ariaHidden?: boolean;
  className?: string;
  displayValue: string;
}
function RollingCount(t0) {
  const $ = _c(24);
  const {
    displayValue,
    className,
    ariaHidden: t1
  } = t0;
  const ariaHidden = t1 === undefined ? false : t1;
  const prefersReducedMotion = useReducedMotion();
  let t2;
  if ($[0] !== prefersReducedMotion) {
    t2 = prefersReducedMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: "-75%"
    };
    $[0] = prefersReducedMotion;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initial = t2;
  let t3;
  if ($[2] !== prefersReducedMotion) {
    t3 = prefersReducedMotion ? {
      opacity: 1
    } : {
      opacity: 1,
      y: "0%"
    };
    $[2] = prefersReducedMotion;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const animate = t3;
  let t4;
  if ($[4] !== prefersReducedMotion) {
    t4 = prefersReducedMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: "75%"
    };
    $[4] = prefersReducedMotion;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const exit = t4;
  let t5;
  if ($[6] !== prefersReducedMotion) {
    t5 = prefersReducedMotion ? {
      duration: 0.12
    } : {
      duration: 0.22,
      ease: [0.16, 1, 0.3, 1] as const
    };
    $[6] = prefersReducedMotion;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const transition = t5;
  const minWidthCh = `${displayValue.length}ch`;
  let t6;
  if ($[8] !== className) {
    t6 = cn("relative inline-flex min-h-[1em] items-center justify-center overflow-hidden leading-tight tabular-nums", className);
    $[8] = className;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== minWidthCh) {
    t7 = {
      minWidth: minWidthCh
    };
    $[10] = minWidthCh;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = ariaHidden ? undefined : "polite";
  let t9;
  if ($[12] !== animate || $[13] !== displayValue || $[14] !== exit || $[15] !== initial || $[16] !== transition) {
    t9 = <AnimatePresence initial={false} mode="popLayout"><motion.span key={displayValue} className="absolute inset-0 flex items-center justify-center" initial={initial} animate={animate} exit={exit} transition={transition}>{displayValue}</motion.span></AnimatePresence>;
    $[12] = animate;
    $[13] = displayValue;
    $[14] = exit;
    $[15] = initial;
    $[16] = transition;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== ariaHidden || $[19] !== t6 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t10 = <span className={t6} style={t7} aria-hidden={ariaHidden} aria-live={t8}>{t9}</span>;
    $[18] = ariaHidden;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}